import React from "react";
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Link,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ConfirmationNumber,
  CheckCircle,
  Email,
  Download,
  Luggage,
  WorkOutline,
  ShoppingBag,
  SupportAgent,
} from "@mui/icons-material";

const RulesInfoSection = () => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();

  const rules = [
    {
      icon: <SupportAgent sx={{ fontSize: 48 }} />,
      title: t("rules.support.title"),
      description: t("rules.support.description"),
      details: [
        { icon: <CheckCircle sx={{ fontSize: 20 }} />, text: t("rules.support.detail1") },
        { icon: <CheckCircle sx={{ fontSize: 20 }} />, text: t("rules.support.detail2") },
        { icon: <CheckCircle sx={{ fontSize: 20 }} />, text: t("rules.support.detail3") },
      ],
      note: { prefix: t("rules.support.notePrefix"), link: t("rules.support.termsLink") },
    },
    {
      icon: <Luggage sx={{ fontSize: 48 }} />,
      title: t("rules.luggage.title"),
      description: t("rules.luggage.description"),
      details: [
        { icon: <WorkOutline sx={{ fontSize: 20 }} />, item: t("rules.luggage.item1"), description: t("rules.luggage.desc1") },
        { icon: <ShoppingBag sx={{ fontSize: 20 }} />, item: t("rules.luggage.item2"), description: t("rules.luggage.desc2") },
        { icon: <Luggage sx={{ fontSize: 20 }} />, item: t("rules.luggage.item3"), description: t("rules.luggage.desc3") },
      ],
      note: { prefix: t("rules.luggage.notePrefix"), link: t("rules.luggage.termsLink") },
    },
    {
      icon: <ConfirmationNumber sx={{ fontSize: 48 }} />,
      title: t("rules.eTicket.title"),
      description: t("rules.eTicket.description"),
      details: [
        { icon: <Email sx={{ fontSize: 20 }} />, text: t("rules.eTicket.detail1") },
        { icon: <Download sx={{ fontSize: 20 }} />, text: t("rules.eTicket.detail2") },
        { icon: <CheckCircle sx={{ fontSize: 20 }} />, text: t("rules.eTicket.detail3") },
      ],
      note: null,
    },
  ];

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        mx: "auto",
        backgroundColor: "background.paper",
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.06)",
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 2, sm: 3 },
      }}
    >
      <Typography
        variant="h5"
        fontWeight="bold"
        textAlign="center"
        mb={1}
        color="text.primary"
        sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}
      >
        {t("rules.title")}
      </Typography>
      <Typography
        variant="body1"
        textAlign="center"
        color="text.secondary"
        mb={4}
      >
        {t("rules.subtitle")}
      </Typography>

      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {rules.map((rule, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card
              variant="outlined"
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                borderRadius: "10px",
                border: "1px solid #e0e0e0",
                transition: "transform 0.3s, box-shadow 0.3s",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.15)",
                },
              }}
            >
              <CardContent sx={{ p: 3, flexGrow: 1, display: "flex", flexDirection: "column" }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    mb: 2,
                    color: "secondary.main",
                  }}
                >
                  {rule.icon}
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    mb: 1,
                    textAlign: "center",
                    color: "text.primary",
                  }}
                >
                  {rule.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#666",
                    textAlign: "center",
                    mb: 2.5,
                    fontSize: "0.875rem",
                  }}
                >
                  {rule.description}
                </Typography>

                <Box sx={{ mt: 2, flexGrow: 1, display: "flex", flexDirection: "column" }}>
                  {rule.details.map((detail, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        mb: idx < rule.details.length - 1 ? 1.5 : 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexWrap: "wrap",
                        gap: 1,
                      }}
                    >
                      {detail.icon && detail.text ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            width: "100%",
                            justifyContent: "flex-start",
                          }}
                        >
                          <Box sx={{ color: "secondary.main", display: "flex", alignItems: "center" }}>
                            {detail.icon}
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#666",
                              fontSize: "0.875rem",
                            }}
                          >
                            {detail.text}
                          </Typography>
                        </Box>
                      ) : detail.icon && detail.item ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1.5,
                            width: "100%",
                            justifyContent: "flex-start",
                          }}
                        >
                          <Box sx={{ color: "secondary.main", display: "flex", alignItems: "center", mt: 0.5 }}>
                            {detail.icon}
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "text.primary",
                                fontWeight: 500,
                                mb: 0.5,
                              }}
                            >
                              {detail.item}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#666",
                                fontSize: "0.8rem",
                              }}
                            >
                              {detail.description}
                            </Typography>
                          </Box>
                        </Box>
                      ) : (
                        <Box sx={{ width: "100%" }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "text.primary",
                              fontWeight: 500,
                              mb: 0.5,
                            }}
                          >
                            {detail.item}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#666",
                              fontSize: "0.8rem",
                            }}
                          >
                            {detail.description}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  ))}
                  {rule.note && (
                    <Typography
                      variant="caption"
                      component="div"
                      sx={{
                        color: "#999",
                        fontStyle: "italic",
                        display: "block",
                        mt: 2,
                        textAlign: "center",
                      }}
                    >
                      {rule.note.prefix}
                      <Link
                        component="button"
                        variant="caption"
                        onClick={() => navigate("/terms-and-conditions")}
                        sx={{
                          color: "secondary.main",
                          textDecoration: "underline",
                          cursor: "pointer",
                          fontStyle: "italic",
                          "&:hover": {
                            color: "#66a03a",
                          },
                        }}
                      >
                        {rule.note.link}
                      </Link>
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default RulesInfoSection;

